import type { Bounds } from '../geom';

/**
 * Who wrote a comment. Anything that arrives over MCP is `ai`, whatever the agent calls
 * itself; its `name` is only a label.
 */
export type CommentAuthor =
  | { readonly kind: 'person'; readonly id: string; readonly name: string }
  | { readonly kind: 'ai'; readonly name: string | null };

export interface DrawingComment {
  readonly id: string;
  readonly author: CommentAuthor;
  /** When it was posted, in milliseconds since the epoch. */
  readonly at: number;
  /** Null once deleted, for a reply kept as a marker so later replies still read. */
  readonly text: string | null;
}

/** A top-level comment, first, and its replies, about a box on the drawing. */
export interface CommentThread {
  readonly id: string;
  /** The box drawn around what the comment is about, in world units. */
  readonly region: Bounds;
  /** Screen pixels per world unit when the box was drawn, so the view can return there. */
  readonly zoom?: number;
  readonly closed: boolean;
  readonly comments: readonly DrawingComment[];
}

export const MAX_COMMENT_LENGTH = 10_000;
export const MAX_AUTHOR_NAME = 60;

/** Why a comment's text cannot be posted, or null when it can. */
export function commentTextProblem(text: string): string | null {
  if (text.trim() === '') return 'A comment needs some text';
  if (text.length > MAX_COMMENT_LENGTH)
    return `A comment is at most ${MAX_COMMENT_LENGTH} characters`;
  return null;
}

/** A box from untrusted input, or null when it is not one. */
export function cleanRegion(value: unknown): Bounds | null {
  const point = (p: unknown) => {
    const { x, y } = (p ?? {}) as { x?: unknown; y?: unknown };
    return typeof x === 'number' && typeof y === 'number' && Number.isFinite(x + y)
      ? { x, y }
      : null;
  };
  const { min: rawMin, max: rawMax } = (value ?? {}) as { min?: unknown; max?: unknown };
  const min = point(rawMin);
  const max = point(rawMax);
  if (!min || !max || min.x > max.x || min.y > max.y) return null;
  return { min, max };
}

/** A zoom from untrusted input, or undefined when it is not one. */
export const cleanZoom = (value: unknown): number | undefined =>
  typeof value === 'number' && Number.isFinite(value) && value > 0 ? value : undefined;

/** Whether a thread waits on an agent: open, with a person's comment last. */
export const waitsOnAgent = (thread: CommentThread): boolean =>
  !thread.closed && thread.comments.at(-1)?.author.kind === 'person';

/**
 * Whether `personId` may delete a comment: their own, and any agent's, since agents reach a
 * drawing only through its owner's connection.
 */
export const canDeleteComment = (comment: DrawingComment, personId: string): boolean =>
  comment.author.kind === 'ai' || comment.author.id === personId;

/**
 * Threads without one comment. The first comment takes its thread with it; a reply with
 * later replies after it becomes a marker with its text erased, and markers left last go.
 */
export function withoutComment(
  threads: readonly CommentThread[],
  threadId: string,
  commentId: string,
): CommentThread[] {
  return threads.flatMap((thread) => {
    if (thread.id !== threadId) return [thread];
    if (thread.comments[0]?.id === commentId) return [];
    const comments = thread.comments.map((comment) =>
      comment.id === commentId ? { ...comment, text: null } : comment,
    );
    while (comments.length > 1 && comments.at(-1)!.text === null) comments.pop();
    return [{ ...thread, comments }];
  });
}
